import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './PatientList.css';

const AppointmentsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Initialize state from URL params or defaults
  const [selectedDate, setSelectedDate] = useState(() => {
    const urlDate = searchParams.get('date');
    if (urlDate) return urlDate;
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [startDate, setStartDate] = useState(() => searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(() => searchParams.get('end_date') || '');
  const [useRange, setUseRange] = useState(() => searchParams.get('use_range') === 'true');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '1', 10));
  const [perPage, setPerPage] = useState(() => parseInt(searchParams.get('per_page') || '20', 10));
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showUnreadAlert, setShowUnreadAlert] = useState(true);
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchAppointments = useCallback(async () => {
    try {
      // Only show loading spinner when no data is present to avoid flicker
      if (appointments.length === 0) {
        setLoading(true);
      }
      setError(null);
      
      // Using HttpOnly cookie-based auth; no token required here

      const params = new URLSearchParams();
      
      if (useRange) {
        // Use date range
        if (startDate) {
          params.append('start_date', startDate);
        }
        if (endDate) {
          params.append('end_date', endDate);
        }
      } else if (selectedDate) {
        // Use single date (backward compatibility)
        const date = new Date(selectedDate + 'T00:00:00Z');
        params.append('date', date.toISOString().split('T')[0]);
      }

      params.append('page', String(page));
      params.append('per_page', String(perPage));

      const response = await axios.get(`${API_URL}appointment-notes?${params.toString()}`);

      let payload = response.data;
      let list = Array.isArray(payload) ? payload : (Array.isArray(payload.data) ? payload.data : []);

      const appointmentsData = list
        .filter(note => note.patient)
        .map(note => ({
          ...note,
          patientId: note.patient.id,
          patientName: note.patient.name || 'Unknown Patient',
          date: note.date || new Date().toISOString(),
          status: note.status
        }));

      setAppointments(appointmentsData);
      if (!Array.isArray(payload)) {
        setTotal(typeof payload.total === 'number' ? payload.total : appointmentsData.length);
        setLastPage(typeof payload.last_page === 'number' ? payload.last_page : 1);
      } else {
        setTotal(appointmentsData.length);
        setLastPage(1);
        setPage(1);
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(err.message || 'Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, startDate, endDate, useRange, page, perPage, API_URL, appointments.length]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Fetch unread messages status
  const fetchUnreadStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}messages/has-unread`, {
        headers: { 'Accept': 'application/json' }
      });
      setHasUnreadMessages(response.data.has_unread || false);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error('Error fetching unread messages status:', err);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchUnreadStatus();
  }, [fetchUnreadStatus]);

  // Auto-refresh appointments every 5 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchAppointments();
    }, 5000);

    // Cleanup on unmount or when dependencies change
    return () => clearInterval(intervalId);
  }, [fetchAppointments]);

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    updateURLParams({ date: newDate, page: 1 });
    setPage(1);
  };

  const handleClearDate = () => {
    setSelectedDate('');
    updateURLParams({ date: '', page: 1 });
    setPage(1);
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    updateURLParams({ start_date: newStartDate, page: 1 });
    setPage(1);
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
    updateURLParams({ end_date: newEndDate, page: 1 });
    setPage(1);
  };

  const handleToggleRange = (e) => {
    const checked = e.target.checked;
    setUseRange(checked);
    // Clear existing filters when switching modes
    if (checked) {
      setSelectedDate('');
      updateURLParams({ use_range: 'true', date: '', page: 1 });
    } else {
      setStartDate('');
      setEndDate('');
      updateURLParams({ use_range: 'false', start_date: '', end_date: '', page: 1 });
    }
    setPage(1);
  };

  const handleClearDateRange = () => {
    setStartDate('');
    setEndDate('');
    updateURLParams({ start_date: '', end_date: '', page: 1 });
    setPage(1);
  };

  const handleStatusChange = async (noteId, newStatus) => {
    try {
      // Find the appointment to get patient ID
      const appointment = appointments.find(app => app.id === noteId);
      if (!appointment) {
        throw new Error('Appointment not found');
      }

      // Update the note status
      const updateData = { status: newStatus };
      await axios.put(`${API_URL}patients/${appointment.patientId}/notes/${noteId}`, updateData, {
        headers: { 'Accept': 'application/json' }
      });

      // Close the dropdown and refresh the appointments list
      setOpenDropdown(null);
      await fetchAppointments();
      
    } catch (err) {
      console.error('Error updating appointment status:', err);
      setError(err.message || 'Failed to update appointment status');
    }
  };

  const toggleDropdown = (appointmentId) => {
    setOpenDropdown(openDropdown === appointmentId ? null : appointmentId);
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  // Helper function to update URL parameters
  const updateURLParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    
    setSearchParams(newParams, { replace: true });
  };

  // Note: Loading and error are handled within the table area to avoid rerendering the whole page

  return (
    <div className="appointments-container">
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-12">
            <div className="card patient-list-card">
              <div className="card-header patient-list-header d-flex justify-content-between align-items-center">
                <h4>Appointments</h4>
                <div>
                  <Link to="/control/patient-create" className="btn create-patient-btn">
                    <i className="fas fa-plus me-2"></i>New Patient
                  </Link>
                </div>
              </div>
              <div className="card-body patient-list-body">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    Error: {error}
                  </div>
                )}

                {hasUnreadMessages && showUnreadAlert && (
                  <div 
                    role="alert"
                    style={{
                      position: 'fixed',
                      top: '80px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1050,
                      minWidth: '320px',
                      maxWidth: '500px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      padding: '1rem 1.25rem',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      animation: 'slideDown 0.3s ease-out'
                    }}
                  >
                    <div style={{ 
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <i className="fas fa-envelope" style={{ fontSize: '1.1rem' }}></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                        New Messages
                      </div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.95 }}>
                        {unreadCount} unread message{unreadCount !== 1 ? 's' : ''} from patients
                      </div>
                    </div>
                    <Link 
                      to="/control/messages" 
                      style={{
                        background: 'white',
                        color: '#667eea',
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      View
                    </Link>
                    <button 
                      type="button" 
                      aria-label="Close"
                      onClick={() => setShowUnreadAlert(false)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'white',
                        fontSize: '1.25rem',
                        cursor: 'pointer',
                        opacity: 0.8,
                        padding: '0.25rem',
                        lineHeight: 1,
                        transition: 'opacity 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.opacity = '1'}
                      onMouseLeave={(e) => e.target.style.opacity = '0.8'}
                    >
                      ×
                    </button>
                  </div>
                )}

          {/* Search and Filter Section - Date Range */}
          <div className="search-filters-section">
            <div className="row mb-3 g-2">
              <div className="col-12">
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="useRangeToggle"
                    checked={useRange}
                    onChange={handleToggleRange}
                  />
                  <label className="form-check-label" htmlFor="useRangeToggle">
                    <i className="fas fa-calendar-alt me-2"></i>
                    Use date range filter
                  </label>
                </div>
              </div>
            </div>
            
            {!useRange ? (
              // Single date filter
              <div className="row mb-3 g-2">
                <div className="col-md-6">
                  <label className="form-label small text-muted">Single Date</label>
                  <div className="input-group">
                    <input
                      type="date"
                      className="form-control date-input"
                      value={selectedDate}
                      onChange={handleDateChange}
                      placeholder="Select date"
                    />
                    {selectedDate && (
                      <button
                        className="btn search-btn"
                        type="button"
                        onClick={handleClearDate}
                        title="Clear date filter"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Date range filter
              <div className="row mb-3 g-2">
                <div className="col-md-5">
                  <label className="form-label small text-muted">From Date</label>
                  <input
                    type="date"
                    className="form-control date-input"
                    value={startDate}
                    onChange={handleStartDateChange}
                    placeholder="Start date"
                  />
                </div>
                <div className="col-md-5">
                  <label className="form-label small text-muted">To Date</label>
                  <input
                    type="date"
                    className="form-control date-input"
                    value={endDate}
                    onChange={handleEndDateChange}
                    placeholder="End date"
                  />
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  {(startDate || endDate) && (
                    <button
                      className="btn search-btn w-100"
                      type="button"
                      onClick={handleClearDateRange}
                      title="Clear date range"
                    >
                      <i className="fas fa-times me-1"></i>
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Table/Empty/Loading Area */}
          {loading && appointments.length === 0 ? (
            <div className="appointments-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading appointments...</p>
            </div>
          ) : appointments.length === 0 ? (
            <div className="no-appointments-state">
              <div className="d-flex flex-column align-items-center">
                <i className="fas fa-calendar-alt no-appointments-icon"></i>
                <h5 className="no-appointments-title">No appointments found</h5>
                <p className="no-appointments-text">
                  {(selectedDate || startDate || endDate) ? (
                    <span>No appointments found for the selected {useRange ? 'date range' : 'date'}. Try adjusting your filter or clear it to see all appointments.</span>
                  ) : (
                    <span>Schedule a new appointment to get started.</span>
                  )}
                </p>
              </div>
            </div>
          ) : (
            <>
            <div className="table-responsive patients-table-container">
              <table className="table table-hover patients-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Patient Name</th>
                    <th className="d-none d-md-table-cell">Appointment Note</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((appointment, index) => (
                    <tr key={appointment.id}>
                      <td>{(page - 1) * perPage + index + 1}</td>
                      <td>
                        <div>
                          <Link 
                            to={`/control/patient/${appointment.patientId}`}
                            className="patient-name-link"
                          >
                            {appointment.patientName}
                          </Link>
                          {appointment.booking_source === 'online' && (
                            <span className="badge bg-info text-dark ms-2" title="Booked online">Online</span>
                          )}
                          <div className="text-muted" style={{fontSize: '0.65rem', marginTop: '2px'}}>
                            {(() => {
                              // Show last update time when available; fallback to appointment date
                              const dateStr = appointment.updated_at || appointment.created_at;
                              if (!dateStr) return '';
                              try {
                                // Detect whether a time component exists in the string
                                const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/);
                                const hasTime = !!(match && match[4]);

                                // Normalize common formats to a parseable ISO string
                                // Accepts: YYYY-MM-DD, YYYY-MM-DD HH:mm[:ss], YYYY-MM-DDTHH:mm[:ss][Z|±HH:MM]
                                let isoCandidate = dateStr;
                                if (match) {
                                  const [ , y, mo, d, hh, mm, ss ] = match;
                                  if (hasTime && !dateStr.includes('T')) {
                                    // Convert "YYYY-MM-DD HH:mm[:ss]" to "YYYY-MM-DDTHH:mm[:ss]"
                                    isoCandidate = `${y}-${mo}-${d}T${hh}:${mm}${ss ? `:${ss}` : ''}`;
                                  }
                                }

                                const dateObj = new Date(isoCandidate);
                                if (isNaN(dateObj.getTime())) return dateStr; // Fallback to raw string

                                const dateFormatter = new Intl.DateTimeFormat('en-US', {
                                  timeZone: 'Asia/Baghdad',
                                  year: 'numeric',
                                  month: 'short',
                                  day: '2-digit'
                                });
                                if (!hasTime) {
                                  return dateFormatter.format(dateObj);
                                }

                                const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
                                  timeZone: 'Asia/Baghdad',
                                  year: 'numeric',
                                  month: 'short',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                });
                                return dateTimeFormatter.format(dateObj);
                              } catch (e) { /* noop */ }
                              return dateStr;
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className="d-none d-md-table-cell">
                        <div className="appointment-description">
                          {appointment.appointment_note}
                        </div>
                      </td>
                      <td>
                        {(() => {
                          // Parse date string directly to avoid timezone issues
                          const dateStr = appointment.date;
                          let appointmentDate;
                          if (dateStr && dateStr.includes('-')) {
                            const [year, month, day] = dateStr.split('-');
                            appointmentDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          } else {
                            appointmentDate = new Date(appointment.date);
                          }
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          appointmentDate.setHours(0, 0, 0, 0);
                          
                          // Determine appointment status based on date and note status
                          const isToday = appointmentDate.getTime() === today.getTime();
                          
                          let statusDisplay = { text: 'Pending', icon: 'fas fa-clock', class: 'status-pending' };
                          
                          // Check if appointment has medical information (description, media, or prescriptions)
                          const hasDescription = appointment.description && appointment.description.trim() !== '';
                          const hasMedia = appointment.media && appointment.media.length > 0;
                          const hasPrescriptions = appointment.prescriptions && appointment.prescriptions.length > 0;
                          const hasMedicalInfo = hasDescription || hasMedia || hasPrescriptions;
                          
                          // Priority order: Medical info (seen) overrides everything else, then manual status
                          if (hasMedicalInfo) {
                            statusDisplay = { text: 'Seen', icon: 'fas fa-check-circle', class: 'status-complete' };
                          } else if (appointment.status === 'checked-in') {
                            statusDisplay = { text: 'Checked In', icon: 'fas fa-user-check', class: 'status-checked-in' };
                          } else if (appointment.status === 'canceled') {
                            statusDisplay = { text: 'Canceled', icon: 'fas fa-times-circle', class: 'status-canceled' };
                          } else {
                            // Default to pending for all other cases
                            statusDisplay = { text: 'Pending', icon: 'fas fa-clock', class: 'status-pending' };
                          }
                          
                          // For today's appointments, make status badge clickable with dropdown
                          if (isToday) {
                            const isOpen = openDropdown === appointment.id;
                            
                            return (
                              <div className="position-relative">
                                <button
                                  className={`status-badge ${statusDisplay.class} clickable-status ${isOpen ? 'active' : ''}`}
                                  onClick={() => toggleDropdown(appointment.id)}
                                  title="Click to change status"
                                  style={{
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <i className={`${statusDisplay.icon} me-1`}></i>
                                  {statusDisplay.text}
                                  <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} ms-2`} style={{fontSize: '0.7rem'}}></i>
                                </button>
                                {isOpen && (
                                  <>
                                    <div 
                                      className="dropdown-backdrop" 
                                      onClick={closeDropdown}
                                    ></div>
                                    <div className="custom-dropdown-menu">
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleStatusChange(appointment.id, 'checked-in')}
                                        style={{
                                          padding: '0.75rem 1rem',
                                          border: 'none',
                                          background: 'transparent',
                                          width: '100%',
                                          textAlign: 'left',
                                          display: 'flex',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          fontSize: '0.875rem',
                                          fontWeight: '500',
                                          color: '#495057',
                                          margin: '0.25rem 0',
                                          borderRadius: '8px',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.background = '#007bff';
                                          e.target.style.color = 'white';
                                          e.target.style.transform = 'translateX(2px)';
                                          e.target.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.2)';
                                          const icon = e.target.querySelector('i');
                                          if (icon) icon.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.background = 'transparent';
                                          e.target.style.color = '#495057';
                                          e.target.style.transform = 'translateX(0)';
                                          e.target.style.boxShadow = 'none';
                                          const icon = e.target.querySelector('i');
                                          if (icon) icon.style.color = '#17a2b8';
                                        }}
                                      >
                                        <i className="fas fa-user-check me-2" style={{color: '#17a2b8'}}></i>
                                        Check In
                                      </button>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleStatusChange(appointment.id, 'canceled')}
                                        style={{
                                          padding: '0.75rem 1rem',
                                          border: 'none',
                                          background: 'transparent',
                                          width: '100%',
                                          textAlign: 'left',
                                          display: 'flex',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          fontSize: '0.875rem',
                                          fontWeight: '500',
                                          color: '#495057',
                                          margin: '0.25rem 0',
                                          borderRadius: '8px',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.background = '#007bff';
                                          e.target.style.color = 'white';
                                          e.target.style.transform = 'translateX(2px)';
                                          e.target.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.2)';
                                          const icon = e.target.querySelector('i');
                                          if (icon) icon.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.background = 'transparent';
                                          e.target.style.color = '#495057';
                                          e.target.style.transform = 'translateX(0)';
                                          e.target.style.boxShadow = 'none';
                                          const icon = e.target.querySelector('i');
                                          if (icon) icon.style.color = '#dc3545';
                                        }}
                                      >
                                        <i className="fas fa-times-circle me-2" style={{color: '#dc3545'}}></i>
                                        Cancel
                                      </button>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleStatusChange(appointment.id, null)}
                                        style={{
                                          padding: '0.75rem 1rem',
                                          border: 'none',
                                          background: 'transparent',
                                          width: '100%',
                                          textAlign: 'left',
                                          display: 'flex',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          fontSize: '0.875rem',
                                          fontWeight: '500',
                                          color: '#495057',
                                          margin: '0.25rem 0',
                                          borderRadius: '8px',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.background = '#007bff';
                                          e.target.style.color = 'white';
                                          e.target.style.transform = 'translateX(2px)';
                                          e.target.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.2)';
                                          const icon = e.target.querySelector('i');
                                          if (icon) icon.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.background = 'transparent';
                                          e.target.style.color = '#495057';
                                          e.target.style.transform = 'translateX(0)';
                                          e.target.style.boxShadow = 'none';
                                          const icon = e.target.querySelector('i');
                                          if (icon) icon.style.color = '#6c757d';
                                        }}
                                      >
                                        <i className="fas fa-sign-out-alt me-2" style={{color: '#6c757d'}}></i>
                                        Reset
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          }
                          
                          // For non-today appointments, show regular status badge
                          return (
                            <span className={`status-badge ${statusDisplay.class}`}>
                              <i className={`${statusDisplay.icon} me-1`}></i>
                              {statusDisplay.text}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mt-3 gap-2">
              <div className="text-muted small">
                {appointments.length > 0 ? (
                  <span>
                    Showing {(page - 1) * perPage + 1}
                    {' '}-{' '}
                    {Math.min(page * perPage, total)} of {total}
                  </span>
                ) : (
                  <span>Showing 0 of {total}</span>
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                <label className="me-2 small text-muted">Rows per page</label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={perPage}
                  onChange={(e) => { 
                    const newPerPage = parseInt(e.target.value, 10);
                    setPage(1); 
                    setPerPage(newPerPage);
                    updateURLParams({ per_page: newPerPage, page: 1 });
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <div className="btn-group">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                      const newPage = Math.max(1, page - 1);
                      setPage(newPage);
                      updateURLParams({ page: newPage });
                    }}
                    disabled={page <= 1}
                  >
                    <i className="fas fa-chevron-left me-1"></i>
                    Prev
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                      const newPage = Math.min(lastPage, page + 1);
                      setPage(newPage);
                      updateURLParams({ page: newPage });
                    }}
                    disabled={page >= lastPage}
                  >
                    Next
                    <i className="fas fa-chevron-right ms-1"></i>
                  </button>
                </div>
                <span className="small text-muted ms-2">Page {page} of {lastPage}</span>
              </div>
            </div>
            </>
          )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentsList;
